import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import usePermissions from "hooks/usePermissions";
import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import Alert from "components/molecules/Alert/Alert";
import { FormattedMessage } from "react-intl";
import { useGetInvoiceConfig } from "api/queries/invoice/useGetInvoiceConfig";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import BillingInvoice from "./components/BillingInvoice/BillingInvoice";
import InvoiceForm from "./components/InvoiceForm/InvoiceForm";

const invoiceSelector = (data: any, isCustomBroker: boolean) => {
  if (isCustomBroker) {
    return data.results.filter((item: any) => item.invoice_type === "customs_broker_invoice");
  }
  return data.results;
};
const initialInvoiceData: any = [];

const ShipmentBilling = function () {
  const { permissions } = usePermissions();
  const { id: shipmentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const [isCustomBroker] = useCustomBrokerPermission();

  const { isLoading, data: invoices = initialInvoiceData } = useGetShipmentInvoices({
    shipmentId: shipmentId!,
    options: { select: useCallback((data: any) => invoiceSelector(data, isCustomBroker), [isCustomBroker]) },
  });

  const currentInvoice = useMemo(
    () => invoices.find((invoice: any) => invoice.invoice_id === currentInvoiceId),
    [currentInvoiceId, invoices],
  );

  useGetInvoiceConfig({ shipmentId: shipmentId!, currentInvoiceId: currentInvoice?.invoice_id });

  useEffect(() => {
    if (invoices?.length && !currentInvoiceId) {
      searchParams.set("invoice", invoices[0].invoice_id);
      setSearchParams(searchParams);
    }
  }, [invoices, currentInvoiceId]);

  useEffect(() => {
    if (permissions && !JSON.parse(permissions).permissions.invoicing.view_invoices) navigate("/access-denied");
  }, [permissions]);

  return (
    <div className="w-100 mx-md">
      {currentInvoice && (
        <>
          {["Declined", "Unsubmitted"].includes(currentInvoice.invoice_status) ? (
            <InvoiceForm invoices={invoices} currentInvoice={currentInvoice} />
          ) : (
            <BillingInvoice isLoading={isLoading} currentInvoice={currentInvoice} totalInvoice={invoices.length} />
          )}
        </>
      )}
      {!isLoading && !currentInvoice && (
        <Alert className="mb-0" variant="danger">
          <FormattedMessage
            id="quote-request.couldNotRetrieveDetailsMsg"
            defaultMessage="We could not retrieve any details. Please try again. If you continue to have trouble please contact admin."
          />
        </Alert>
      )}
    </div>
  );
};

export default ShipmentBilling;
